import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from './ui/sheet';
import SvgLogo from '../imports/SvgLogo1';

interface HeaderProps {
  cartItemCount: number;
  onCartClick: () => void;
  onGalleryClick: () => void;
  onHomeClick: () => void;
  onAboutClick?: () => void;
  onExploreClick?: () => void;
  onPrintsClick?: () => void;
  showExploreAlways?: boolean;
  transparent?: boolean;
}

export function Header({ cartItemCount, onCartClick, onGalleryClick, onHomeClick, onAboutClick, onExploreClick, onPrintsClick, showExploreAlways = false, transparent = false }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [hideChrome, setHideChrome] = useState(false);
  const lastScrollY = useRef(0);
  const iconColor = transparent ? 'white' : 'black';

  useEffect(() => {
    const hasHero = onExploreClick && !showExploreAlways;
    const handleScroll = () => {
      const currentY = window.scrollY;
      const heroThreshold = hasHero ? window.innerHeight * 0.85 : 80;
      setPastHero(currentY > heroThreshold);

      // Hide logo/menu when scrolling DOWN past threshold, show when scrolling UP
      if (currentY > heroThreshold) {
        if (currentY > lastScrollY.current + 5) {
          setHideChrome(true);
        } else if (currentY < lastScrollY.current - 5) {
          setHideChrome(false);
        }
      } else {
        setHideChrome(false);
      }
      lastScrollY.current = currentY;
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [onExploreClick, showExploreAlways]);

  const handleNavClick = (action: () => void) => {
    setMenuOpen(false);
    // Delay navigation so Sheet can animate closed before potential unmount
    setTimeout(() => action(), 250);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 ${transparent ? 'border-b border-white/10' : 'bg-white border-b border-neutral-200'}`}>
      {/* Mobile: 110px height for bigger logo, Desktop: 36px height */}
      <div className="container mx-auto flex items-center justify-between px-4 lg:px-8 h-[110px] lg:h-[36px]">
        <div className={`flex items-center transition-all duration-300 ${hideChrome ? 'opacity-0 -translate-x-4 pointer-events-none' : 'opacity-100 translate-x-0'}`}>
          <button onClick={onHomeClick} className="hover:opacity-60 transition-opacity focus:outline-none">
            {/* Mobile: 100px LOGO, Desktop: 16px */}
            <div className="w-[64px] h-[64px] lg:w-[10px] lg:h-[10px]" style={{ '--fill-0': transparent ? 'white' : 'black' } as React.CSSProperties}>
              <div style={{ transform: 'scaleX(0.75)' }}>
                <SvgLogo />
              </div>
            </div>
          </button>
        </div>
        
        {/* Center: Explore Collection */}
        {onExploreClick && (
          <div className={`absolute left-1/2 -translate-x-1/2 transition-all duration-500 ${(pastHero || showExploreAlways) ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
            <button
              onClick={onExploreClick}
              className={`tracking-[0.2em] uppercase font-light hover:opacity-60 transition-opacity focus:outline-none ${transparent ? 'text-white/80' : 'text-neutral-600'} text-[13px]`}
            >
              Art Store
            </button>
          </div>
        )}
        
        <div className={`flex items-center gap-4 lg:gap-6 transition-all duration-300 ${hideChrome ? 'opacity-0 translate-x-4 pointer-events-none' : 'opacity-100 translate-x-0'}`}>
          {/* CART ICON - Only shows when items in cart */}
          {cartItemCount > 0 && (
            <div className="relative">
              <button 
                onClick={onCartClick}
                className="hover:opacity-60 transition-opacity focus:outline-none"
              >
                {/* MOBILE - 50px icons */}
                <svg className="lg:hidden" width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="9" cy="21" r="1" stroke={iconColor} strokeWidth="2" fill="none"/>
                  <circle cx="20" cy="21" r="1" stroke={iconColor} strokeWidth="2" fill="none"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                {/* DESKTOP - 32px icons */}
                <svg className="hidden lg:block" width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision">
                  <circle cx="9" cy="21" r="1" stroke={iconColor} strokeWidth="0.8" fill="none" vectorEffect="non-scaling-stroke" />
                  <circle cx="20" cy="21" r="1" stroke={iconColor} strokeWidth="0.8" fill="none" vectorEffect="non-scaling-stroke" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" stroke={iconColor} strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" fill="none" vectorEffect="non-scaling-stroke" />
                </svg>
              </button>
              <div 
                className={`absolute flex items-center justify-center text-[16px] font-bold lg:text-[11px] lg:font-normal rounded-full ${transparent ? 'bg-white text-black' : 'bg-black text-white'} w-[32px] h-[32px] -top-1 -right-1 lg:w-[20px] lg:h-[20px] lg:top-0 lg:right-0`}
              >
                {cartItemCount}
              </div>
            </div>
          )}

          {/* MENU ICON */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button className="hover:opacity-60 transition-opacity focus:outline-none">
                {/* MOBILE - 50px icons */}
                <svg className="lg:hidden ml-2" width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="3" y1="12" x2="21" y2="12" stroke={iconColor} strokeWidth="1.6" strokeLinecap="round"/>
                  <line x1="3" y1="6" x2="21" y2="6" stroke={iconColor} strokeWidth="1.6" strokeLinecap="round"/>
                  <line x1="3" y1="18" x2="21" y2="18" stroke={iconColor} strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
                {/* DESKTOP - 32px icons */}
                <svg className="hidden lg:block" width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision">
                  <line x1="3" y1="12" x2="21" y2="12" stroke={iconColor} strokeWidth="0.6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                  <line x1="3" y1="6" x2="21" y2="6" stroke={iconColor} strokeWidth="0.6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                  <line x1="3" y1="18" x2="21" y2="18" stroke={iconColor} strokeWidth="0.6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                </svg>
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white/5 backdrop-blur-md border-l border-white/10">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <SheetDescription className="sr-only">Navigation menu options</SheetDescription>
              <div className="absolute inset-0 flex items-center justify-center pl-20 pb-32">
                <nav className="flex flex-col gap-8 items-center w-full">
                  <button 
                    onClick={() => handleNavClick(onHomeClick)} 
                    className="text-center text-lg tracking-wide hover:opacity-60 transition-opacity focus:outline-none text-white w-full"
                  >
                    Home
                  </button>
                  <button 
                    onClick={() => handleNavClick(onGalleryClick)} 
                    className="text-center text-lg tracking-wide hover:opacity-60 transition-opacity focus:outline-none text-white w-full"
                  >
                    Art Store
                  </button>
                  <button 
                    onClick={() => handleNavClick(onAboutClick || (() => {}))} 
                    className="text-center text-lg tracking-wide hover:opacity-60 transition-opacity focus:outline-none text-white w-full"
                  >
                    About
                  </button>
                  <button 
                    onClick={() => handleNavClick(onPrintsClick || onGalleryClick)} 
                    className="text-center text-lg tracking-wide hover:opacity-60 transition-opacity focus:outline-none text-white w-full"
                  >
                    Prints
                  </button>
                </nav>
              </div>

              {/* Paper info - bottom of menu */}
              <div className="absolute bottom-10 left-0 right-0 px-8">
                <p className="text-[10px] tracking-[2px] uppercase text-white/25 text-center mb-1">Paper</p>
                <p className="text-[11px] text-white/20 text-center font-light">Hahnemühle Photo Rag · 310gsm</p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export default Header;